import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { confirm } from "@/lib/confirm";
import ErrorBoundary from "@/components/ErrorBoundary";

import PatientHeader from "./patientDetail/PatientHeader";
import ResumoTab from "./patientDetail/ResumoTab";
import DadosTab from "./patientDetail/DadosTab";
import HistoricoCirurgicoTab from "./patientDetail/HistoricoCirurgicoTab";
import DocumentosTab from "./patientDetail/DocumentosTab";

/** Orchestrator for /pacientes/:id — fetches patient + cases + doc types,
 * delegates rendering to focused tab components. */
export default function PatientDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [patient, setPatient] = useState(null);
  const [cases, setCases] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [uploading, setUploading] = useState(false);

  async function reload() {
    try {
      const r = await api.get(`/patients/${id}`);
      setPatient(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }

  useEffect(() => {
    reload();
    api.get(`/patients/${id}/cases`).then((r) => setCases(r.data)).catch(() => {});
    api.get(`/patient-document-types`).then((r) => setDocTypes(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function onUpload({ file, type, notes }) {
    if (!file) { toast.error("Selecione um arquivo"); return; }
    if (file.size > 25 * 1024 * 1024) { toast.error("Arquivo muito grande (max 25 MB)"); return; }
    const fd = new FormData();
    fd.append("doc_type", type);
    if (notes) fd.append("notes", notes);
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(`/patients/${id}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Documento enviado");
      await reload();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setUploading(false); }
  }

  async function onDelete(aid) {
    if (!(await confirm({ description: "Excluir este anexo?" }))) return;
    try {
      await api.delete(`/patients/${id}/attachments/${aid}`);
      toast.success("Anexo removido");
      await reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }

  async function onDownload(att) {
    try {
      const r = await api.get(att.file_url.replace(/^\/api/, ""), { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.original_filename || att.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }

  if (!patient) return <div className="text-slate-500">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="patient-detail">
      <Button variant="ghost" size="sm" onClick={() => nav(-1)} data-testid="btn-back">
        <ArrowLeft className="h-4 w-4 mr-1" /> Voltar
      </Button>

      <PatientHeader patient={patient} />

      <Tabs defaultValue="resumo">
        <TabsList className="bg-white border border-slate-200 overflow-x-auto flex-wrap">
          <TabsTrigger value="resumo" data-testid="tab-resumo">Resumo</TabsTrigger>
          <TabsTrigger value="dados" data-testid="tab-dados">Dados Pessoais</TabsTrigger>
          <TabsTrigger value="historico" data-testid="tab-historico">Histórico Cirúrgico</TabsTrigger>
          <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos</TabsTrigger>
        </TabsList>

        <TabsContent value="resumo">
          <ErrorBoundary inline label="Resumo indisponível" testid="patient-resumo-error">
            <ResumoTab patient={patient} cases={cases} />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="dados">
          <ErrorBoundary inline label="Dados pessoais indisponíveis" testid="patient-dados-error">
            <DadosTab patient={patient} />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="historico">
          <ErrorBoundary inline label="Histórico cirúrgico indisponível" testid="patient-historico-error">
            <HistoricoCirurgicoTab cases={cases} />
          </ErrorBoundary>
        </TabsContent>

        <TabsContent value="documentos">
          <ErrorBoundary inline label="Documentos indisponíveis" testid="patient-documentos-error">
            <DocumentosTab
              patient={patient}
              docTypes={docTypes}
              uploading={uploading}
              onUpload={onUpload}
              onDownload={onDownload}
              onDelete={onDelete}
            />
          </ErrorBoundary>
        </TabsContent>
      </Tabs>
    </div>
  );
}
